import React, { useState, useEffect, Fragment } from "react";
import YouTube from "react-youtube";
import { XMarkIcon, PlayIcon, PauseIcon, MusicalNoteIcon, MinusIcon, PlusIcon, RectangleStackIcon } from "@heroicons/react/20/solid";
import { usePostHog } from 'posthog-js/react';

interface SavedYouTubeURL {
  url: string;
  timestamp: number;
  title?: string;
}

// Preset URLs for recommended focus music
const PRESET_URLS = [
  { title: "Lofi Girl - Beats to Relax/Study", url: "https://www.youtube.com/watch?v=jfKfPfyJRdk" },
  { title: "Relaxing Piano Music 24/7 Live", url: "https://www.youtube.com/watch?v=4khIPP--FDU" },
  { title: "Jazz Music - Relaxing Cafe Music", url: "https://www.youtube.com/watch?v=Dx5qFachd3A" },
  { title: "Chillhop Radio - Beats to Study/Relax", url: "https://www.youtube.com/watch?v=5yx6BWlEVcY" },
];

interface YouTubePlayerProps {
  isVisible: boolean;
  onClose: () => void;
  onImmersiveChange?: (isImmersive: boolean) => void;
  isAppMinimized?: boolean;
  isImmersive?: boolean;
  hasActiveTasks?: boolean;
}

const YouTubePlayer: React.FC<YouTubePlayerProps> = ({
  isVisible,
  onClose,
  onImmersiveChange,
  isAppMinimized = false,
  isImmersive: isImmersiveProp = false,
  hasActiveTasks = false,
}) => {
  const posthog = usePostHog();
  const [url, setUrl] = useState("");
  const [videoId, setVideoId] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [player, setPlayer] = useState<any>(null);
  const [isReady, setIsReady] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isImmersive, setIsImmersive] = useState(false);
  const [currentVideoTitle, setCurrentVideoTitle] = useState<string>("");
  const [isMounted, setIsMounted] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);
  const [savedUrls, setSavedUrls] = useState<SavedYouTubeURL[]>(() => {
    const saved = localStorage.getItem("savedYouTubeUrls");
    return saved ? JSON.parse(saved) : [];
  });
  const [isLoadingTitle, setIsLoadingTitle] = useState(false);

  // Track screen size for responsive positioning
  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Save URLs to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("savedYouTubeUrls", JSON.stringify(savedUrls));
  }, [savedUrls]);

  const fetchVideoTitle = async (videoUrl: string): Promise<string | null> => {
    try {
      // Use YouTube's oEmbed API to get video title
      const oembedUrl = `https://www.youtube.com/oembed?url=${encodeURIComponent(videoUrl)}&format=json`;
      const response = await fetch(oembedUrl);
      
      if (!response.ok) {
        throw new Error('Failed to fetch video title');
      }
      
      const data = await response.json();
      return data.title || null;
    } catch (error) {
      console.error('Error fetching video title:', error);
      return null;
    }
  };

  const saveCurrentUrl = async () => {
    if (!url) return;

    // Check if URL already exists
    const exists = savedUrls.some((saved) => saved.url === url);
    if (exists) return;

    setIsLoadingTitle(true);
    
    try {
      // Fetch video title
      const title = await fetchVideoTitle(url);

      const newSavedUrl: SavedYouTubeURL = {
        url,
        timestamp: Date.now(),
        title: title || undefined,
      };

      setSavedUrls((prev) => {
        // Keep only the last 5 URLs
        const updated = [newSavedUrl, ...prev].slice(0, 5);
        return updated;
      });
    } catch (error) {
      console.error('Error saving URL:', error);
      // Still save the URL even if title fetching fails
      const newSavedUrl: SavedYouTubeURL = {
        url,
        timestamp: Date.now(),
      };

      setSavedUrls((prev) => {
        const updated = [newSavedUrl, ...prev].slice(0, 5);
        return updated;
      });
    } finally {
      setIsLoadingTitle(false);
    }
  };

  const removeSavedUrl = (urlToRemove: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSavedUrls((prev) => prev.filter((saved) => saved.url !== urlToRemove));
  };



  const extractVideoId = (url: string) => {
    // Handle various YouTube URL formats
    const patterns = [
      // Standard watch URLs
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^?&#]+)/,
      // Live stream URLs
      /youtube\.com\/live\/([^?&#]+)/,
      // Embed URLs
      /youtube\.com\/embed\/([^?&#]+)/,
      // Short URLs
      /youtu\.be\/([^?&#]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  };

  const handleUrlSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const id = extractVideoId(url);
    if (id) {
      setVideoId(id);
      setIsPlaying(false);
      setIsReady(false);
      
      // Try to get title for current video
      const title = await fetchVideoTitle(url);
      setCurrentVideoTitle(title || "YouTube Video");
      
      await saveCurrentUrl(); // Save URL with title when successfully loaded
      
      // Clear the URL field after successful loading
      setUrl("");
    } else {
      alert("Please enter a valid YouTube URL");
    }
  };

  const onReady = (event: any) => {
    setPlayer(event.target);
    setIsReady(true);
    event.target.unMute();
    event.target.setVolume(50);
  };

  const onStateChange = (event: any) => {
    if (event.data === 1) {
      setIsPlaying(true);
      
      // Track YouTube video played
      posthog?.capture('youtube_video_played', {
        video_id: videoId,
        source: savedUrls.some(saved => extractVideoId(saved.url) === videoId) ? 'saved' : 'custom'
      });
    } else if (event.data === 2) {
      setIsPlaying(false);
    }
  };

  const onError = (event: any) => {
    console.error("YouTube player error:", event);
  };

  const togglePlayPause = () => {
    try {
      if (player && isReady && typeof player.pauseVideo === 'function' && typeof player.playVideo === 'function') {
        if (isPlaying) {
          player.pauseVideo();
        } else {
          player.playVideo();
        }
      } else {
        console.warn('YouTube player not ready or methods not available');
      }
    } catch (error) {
      console.error('Error toggling play/pause:', error);
    }
  };

  useEffect(() => {
    return () => {
      if (player) {
        player.destroy();
      }
    };
  }, [player]);

  // Reset minimize and immersive state when no video is loaded
  useEffect(() => {
    if (!videoId) {
      setIsMinimized(false);
      setIsImmersive(false);
    }
  }, [videoId]);

  // Sync internal immersive state with prop from parent
  useEffect(() => {
    setIsImmersive(isImmersiveProp);
  }, [isImmersiveProp]);

  // Trigger mount animation for modal
  useEffect(() => {
    if (isVisible && !isMinimized) {
      // Slight delay to ensure DOM is ready for transition
      const timer = setTimeout(() => setIsMounted(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsMounted(false);
    }
  }, [isVisible, isMinimized]);

  // Handle immersive mode toggle - simplified to avoid playback interruption
  const enterImmersiveMode = () => {
    setIsImmersive(true);
    setIsMinimized(false); // Ensure we're not minimized
    onImmersiveChange?.(true);
  };

  const clearAllSavedUrls = () => {
    setSavedUrls([]);
    localStorage.removeItem("savedYouTubeURLs");
  };

  const loadSavedUrl = async (url: string) => {
    setVideoId(extractVideoId(url) || "");
    setIsPlaying(false);
    setIsReady(false);
    
    // Get the saved URL info
    const savedUrl = savedUrls.find(saved => saved.url === url);
    
    // Set current video title from saved data or fetch if not available
    if (savedUrl?.title) {
      setCurrentVideoTitle(savedUrl.title);
    } else {
      const title = await fetchVideoTitle(url);
      const finalTitle = title || "YouTube Video";
      setCurrentVideoTitle(finalTitle);
      
      // Update saved URLs with the fetched title
      if (title) {
        setSavedUrls(prev => 
          prev.map(saved => 
            saved.url === url ? { ...saved, title } : saved
          )
        );
      }
    }
    
    // Clear the URL field after loading from recently played
    setUrl("");
  };

  if (!isVisible && !isImmersive) return null;

  // Render immersive mode styling when active
  if (isImmersive && videoId) {
    return (
      <>
        {/* Background Video Container */}
        <div className="absolute inset-0 flex items-center justify-center bg-black z-10">
          <div className="w-full h-full flex items-center justify-center">
            <div className="max-w-full max-h-full"
              style={{
                aspectRatio: '16/9',
                width: '100vw',
                height: '56.25vw', // 16:9 ratio
                maxHeight: '100vh',
                maxWidth: '177.78vh' // 16:9 ratio
              }}
            >
              <YouTube
                videoId={videoId}
                opts={{
                  height: "100%",
                  width: "100%",
                  playerVars: {
                    autoplay: 1,
                    controls: 0,
                    modestbranding: 1,
                    rel: 0,
                    showinfo: 0,
                    iv_load_policy: 3,
                    fs: 0,
                    cc_load_policy: 0,
                    disablekb: 1,
                  },
                }}
                onReady={onReady}
                onStateChange={onStateChange}
                onError={onError}
                className="w-full h-full"
                key={`immersive-${videoId}`}
              />
            </div>
          </div>
        </div>

        {/* Exit button now rendered at App.tsx level to avoid z-index stacking context issues */}
      </>
    );
  }

  // Render mini player when minimized and has active video
  const renderMiniPlayer = () => {
    if (!isMinimized || !videoId) return null;
    
    // Determine bottom position based on screen size, task bar visibility, AND app minimized state
    const getBottomClass = () => {
      if (!hasActiveTasks) {
        // No task bar visible - sit close to bottom
        return 'bottom-4';
      }
      
      // Task bar is visible - need clearance based on its state
      if (isAppMinimized) {
        // TaskPlayerBar is in compact/pill mode (~70px tall)
        return isLargeScreen ? 'bottom-24' : 'bottom-28';
      } else {
        // TaskPlayerBar is in expanded/full mode (~200px tall)
        return isLargeScreen ? 'bottom-52' : 'bottom-56';
      }
    };
    
    return (
      <div
        className={`fixed bg-white dark:bg-dark-card rounded-full shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-white dark:ring-opacity-10 border border-gray-200 dark:border-gray-700 transition-all duration-200 hover:shadow-xl ${getBottomClass()}
          ${isLargeScreen ? 'right-4' : 'left-1/2 -translate-x-1/2'}`}
        style={{ zIndex: 1000, minWidth: '280px', maxWidth: '320px' }}
      >
        <div className="flex items-center space-x-3 pl-4 pr-2 py-3">
          {/* Play/Pause Button */}
          <button
            onClick={togglePlayPause}
            className={`p-2 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              isPlaying
                ? "bg-red-50 text-red-600 hover:bg-red-100 focus:ring-red-500 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                : "bg-green-50 text-green-600 hover:bg-green-100 focus:ring-green-500 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/30"
            }`}
          >
            {isPlaying ? (
              <PauseIcon className="h-4 w-4" />
            ) : (
              <PlayIcon className="h-4 w-4" />
            )}
          </button>
          
          {/* Song Title */}
          <div 
            onClick={() => setIsMinimized(false)}
            className="flex-1 min-w-0 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200"
          >
            <p className="text-sm font-medium text-gray-900 dark:text-dark-primary truncate" title={currentVideoTitle}>
              {currentVideoTitle || "Playing..."}
            </p>
          </div>
          
          {/* Expand Button */}
          <button
            onClick={() => setIsMinimized(false)}
            className="p-2 text-gray-400 hover:text-gray-600 dark:text-dark-secondary dark:hover:text-dark-primary hover:bg-gray-100 dark:hover:bg-dark-hover rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            title="Expand"
          >
            <PlusIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Mini Player */}
      {renderMiniPlayer()}
      
      {/* Full Player - Always bottom-right when not immersive */}
      {!isImmersive && (
        <>
          {/* Mobile: Full-screen backdrop */}
          <div 
            className="sm:hidden fixed inset-0 bg-black/50 z-[999] transition-opacity duration-300"
            onClick={onClose}
            style={{ 
              display: isMinimized ? 'none' : 'block',
              opacity: isMounted ? 1 : 0
            }}
          />
          
          <div
            className={`fixed bg-white dark:bg-dark-card shadow-xl ring-1 ring-black ring-opacity-5 dark:ring-white dark:ring-opacity-10 p-6 border border-gray-200 dark:border-gray-700 transition-all duration-300 ease-out flex flex-col
                        sm:bottom-4 sm:right-4 sm:w-[400px] sm:max-h-[85vh] sm:rounded-xl
                        max-sm:inset-x-0 max-sm:bottom-0 max-sm:rounded-t-2xl max-sm:max-h-[80vh] max-sm:pb-safe ${
              isMinimized ? 'opacity-0 pointer-events-none' : 'opacity-100'
            }`}
            style={{ 
              zIndex: isMinimized ? -1 : 1000,
              transform: window.innerWidth < 640 && !isMinimized ? (isMounted ? 'translateY(0)' : 'translateY(100%)') : undefined,
            }}
          >
      <div className="flex justify-between items-center mb-6 flex-shrink-0">
        <div className="flex items-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-primary">Background Music</h3>
        </div>
        <div className="flex items-center space-x-1">
          {videoId && (
            <>
              <button 
                onClick={enterImmersiveMode}
                className="p-2 text-gray-400 hover:text-gray-500 dark:text-dark-secondary dark:hover:text-dark-primary hover:bg-gray-100 dark:hover:bg-dark-hover rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                title="Full Screen"
              >
                <RectangleStackIcon className="h-5 w-5" />
              </button>
              <button 
                onClick={() => setIsMinimized(true)}
                className="p-2 text-gray-400 hover:text-gray-500 dark:text-dark-secondary dark:hover:text-dark-primary hover:bg-gray-100 dark:hover:bg-dark-hover rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                title="Minimize"
              >
                <MinusIcon className="h-5 w-5" />
              </button>
            </>
          )}
          <button 
            onClick={onClose} 
            className="p-2 text-gray-400 hover:text-gray-500 dark:text-dark-secondary dark:hover:text-dark-primary hover:bg-gray-100 dark:hover:bg-dark-hover rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            title="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="overflow-y-auto flex-1 pr-1 -mr-1 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">

      {videoId ? (
        <div className="relative pb-[56.25%] h-0 overflow-hidden bg-gray-100 dark:bg-dark-hover rounded-xl border border-gray-200 dark:border-gray-700 mb-6">
          <YouTube
            videoId={videoId}
            opts={{
              height: "100%",
              width: "100%",
              playerVars: {
                autoplay: 1,
                controls: 1,
                modestbranding: 1,
                rel: 0,
              },
            }}
            onReady={onReady}
            onStateChange={onStateChange}
            onError={onError}
            className="absolute top-0 left-0 w-full h-full rounded-xl"
            key={`normal-${videoId}`}
          />
        </div>
      ) : (
        <div className="relative pb-[56.25%] h-0 flex items-center justify-center bg-gray-50 dark:bg-dark-hover text-gray-500 dark:text-dark-secondary rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 mb-6">
          <div className="absolute inset-0 flex flex-col items-center justify-center space-y-2">
            <MusicalNoteIcon className="h-8 w-8 text-gray-400 dark:text-gray-500" />
            <p className="text-sm font-medium">Enter a YouTube URL to load a video</p>
            <p className="text-xs text-gray-400 dark:text-gray-500">Music videos, ambient sounds, or focus playlists</p>
          </div>
        </div>
      )}

      <form onSubmit={handleUrlSubmit} className="mb-6">
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-secondary">
            YouTube URL
          </label>
          <div className="flex gap-3">
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="flex-1 px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-dark-card text-gray-900 dark:text-dark-primary placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-colors duration-200"
              placeholder="https://youtube.com/watch?v=..."
            />
            <button
              type="submit"
              className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white font-medium text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!url.trim() || isLoadingTitle}
            >
              {isLoadingTitle ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Loading...</span>
                </div>
              ) : (
                'Load'
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Recommended Presets */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mb-6">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-dark-primary mb-4">
          Recommended
        </h4>
        <div className="space-y-2">
          {PRESET_URLS.map((preset) => (
            <div
              key={preset.url}
              onClick={() => loadSavedUrl(preset.url)}
              className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-700/50 hover:from-blue-100 hover:to-indigo-100 dark:hover:from-blue-800/30 dark:hover:to-indigo-800/30 transition-all duration-200 cursor-pointer group shadow-sm hover:shadow-md"
            >
              <div className="flex items-center min-w-0">
                <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-blue-500 dark:bg-blue-600 rounded-full text-white group-hover:scale-110 transition-transform duration-200">
                  <PlayIcon className="h-4 w-4" />
                </span>
                <span className="ml-3 truncate text-sm font-medium text-gray-700 dark:text-dark-primary group-hover:text-blue-700 dark:group-hover:text-blue-400 transition-colors">
                  {preset.title}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {savedUrls.length > 0 && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-dark-primary">Recently Played</h4>
            <button
              onClick={clearAllSavedUrls}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors duration-200"
            >
              Clear All
            </button>
          </div>
          <div className="space-y-2 max-h-40 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
            {savedUrls.map((saved) => (
              <div 
                key={saved.timestamp}
                onClick={() => loadSavedUrl(saved.url)}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-hover rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors duration-200 cursor-pointer"
              >
                <span className="truncate flex-1 text-sm text-gray-700 dark:text-dark-primary mr-3" title={saved.title || saved.url}>
                  {saved.title || `YouTube Video • ${new Date(saved.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                </span>
                <button
                  onClick={(e) => removeSavedUrl(saved.url, e)}
                  className="p-1.5 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors duration-200"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
          </div>
        </>
      )}
    </>
  );
};

export default YouTubePlayer;
